import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Chip, Button } from '@mui/material';
import { useLanguage } from '../../../../context/LanguageContext';
import { Store } from '../../../../types/Store';
import { EditStoreInfoDrawer } from '../../UpdateStore/components/Drawers';

interface StoreInfoProps {
    store: Store;
    isEditable?: boolean;
}

const InfoRow = ({ label, value }: { label: string; value?: string | number | null | React.ReactNode }) => (
    <tr>
        <td style={{ padding: '8px 0', verticalAlign: 'top' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        </td>
        <td style={{ padding: '8px 0', paddingLeft: '16px' }}>
            {typeof value === 'string' || typeof value === 'number' ? (
                <Typography variant="body2">{value || '-'}</Typography>
            ) : (
                value || <Typography variant="body2">-</Typography>
            )}
        </td>
    </tr>
);

const StoreInfo = ({ store, isEditable = false }: StoreInfoProps) => {
    const { t } = useLanguage();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const getEntityTypeColor = (): "success" | "info" | "default" => {
        switch (store.ENTITY_TYPE) {
            case 'COMPANY':
            case 'ROOT':
                return 'success';
            case 'STATISTIC':
                return 'info';
            default:
                return 'default';
        }
    };

    const franchiseLabel = store.FRANCHISE === '1' ? 'FRANCHISE' : 'INTEGRE';

    // Vérifier si le magasin est ouvert ou fermé
    const isOpen = !store.UNSUBSCRIBE_DATE || new Date(store.UNSUBSCRIBE_DATE) > new Date();

    return (
        <>
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Typography variant="h6" component="h2">
                            {store.NAME} <Typography component="span" variant="body2" color="text.secondary">(#{store.ENTITY_ID})</Typography>
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                                label={isOpen ? t('pages.store.open') : t('pages.store.closed')} 
                                color={isOpen ? 'success' : 'error'} 
                                size="small" 
                            />
                            {isEditable && (
                                <Button 
                                    size="small" 
                                    variant="text"
                                    color="primary" 
                                    onClick={() => setDrawerOpen(true)}
                                >
                                    {t('common.edit')}
                                </Button>
                            )}
                        </Box>
                    </Box>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                            <InfoRow label={t('pages.store.storeNumber')} value={store.IDENTIFIER} />
                            <InfoRow label={t('pages.store.internalCode')} value={store.ENTITY_NUMBER} />
                            <InfoRow label={t('pages.store.type')} value={<Chip label={(store.ENTITY_TYPE || 'STORE').toString().toUpperCase()} color={getEntityTypeColor()} size="small" />} />
                            <InfoRow label={t('pages.store.franchise')} value={<Chip label={franchiseLabel} color="info" size="small" />} />
                            <InfoRow label={t('pages.store.transactionMode')} value={store.TRANSACTION_INTEGRATION_MODE === 'WS' ? <Chip label="WS" color="info" size="small" /> : store.TRANSACTION_INTEGRATION_MODE === '1' ? <Chip label={t('common.yes')} color="success" size="small" /> : <Chip label={t('common.no')} color="error" size="small" />} />
                            <InfoRow label={t('pages.store.advancePayment')} value={store.PAYMENT_OVERDUE === '1' ? <Chip label={t('common.yes')} color="success" size="small" /> : <Chip label={t('common.no')} color="error" size="small" />} />
                        </tbody>
                    </table>
                </CardContent>
            </Card>
            
            <EditStoreInfoDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                store={store}
            />
        </>
    );
};

export default StoreInfo;
